import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { categories, getCategoryBySlug } from '@/mocks/categories';
import { getVideosByCategory } from '@/mocks/videos';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const category = getCategoryBySlug(slug || '');
  const categoryVideos = getVideosByCategory(slug || '');
  const [sortBy, setSortBy] = useState<'hot' | 'new' | 'rating'>('hot');

  const sortedVideos = [...categoryVideos].sort((a, b) => {
    if (sortBy === 'rating') return b.rating - a.rating;
    if (sortBy === 'new') return b.year - a.year;
    return (b.hot ? 1 : 0) - (a.hot ? 1 : 0);
  });

  if (!category) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-200">
            <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
          </div>
          <h2 className="text-xl font-bold text-foreground-800 mb-2">分类未找到</h2>
          <p className="text-foreground-500 mb-4">请检查链接是否正确</p>
          <Link to="/" className="text-primary-500 hover:text-primary-600 font-medium cursor-pointer whitespace-nowrap">
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const structuredData = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebPage',
        name: `${category.name} - 极速影视`,
        description: `${category.description}，极速不缓冲高清在线观看`,
        url: `https://chentaotao.cn/category/${slug}`,
        isPartOf: { '@type': 'WebSite', name: '极速影视' },
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://chentaotao.cn' },
          { '@type': 'ListItem', position: 2, name: category.name },
        ],
      },
    ],
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
      />

      <div className="pt-20 min-h-screen bg-background-50">
        <div className="w-full px-4 md:px-6 py-8">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb items={[{ label: category.name }]} />

            <div className="mt-6 mb-8">
              <div className="flex items-center gap-4 mb-2">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-primary-100">
                  <i className={`${category.icon} text-2xl text-primary-600`}></i>
                </div>
                <div>
                  <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
                    {category.name}
                  </h1>
                  <p className="text-sm text-foreground-500 mt-1">{category.description}</p>
                </div>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2 mb-8">
              <span className="text-sm text-foreground-500 mr-2">分类切换：</span>
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-all ${
                    slug === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>

            <div className="flex items-center gap-3 mb-8">
              <span className="text-sm text-foreground-500">排序：</span>
              {[
                { value: 'hot' as const, label: '热门优先' },
                { value: 'new' as const, label: '最新上线' },
                { value: 'rating' as const, label: '评分最高' },
              ].map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => setSortBy(opt.value)}
                  className={`px-4 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap cursor-pointer transition-all ${
                    sortBy === opt.value
                      ? 'bg-secondary-100 text-secondary-700'
                      : 'text-foreground-500 hover:text-foreground-700 hover:bg-background-100'
                  }`}
                >
                  {sortBy === opt.value && <i className="ri-check-line mr-1"></i>}
                  {opt.label}
                </button>
              ))}
            </div>

            {sortedVideos.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {sortedVideos.map((video) => (
                  <Link
                    key={video.id}
                    to={`/category/${video.category}`}
                    className="group bg-background-50 border border-background-200 rounded-2xl overflow-hidden cursor-pointer transition-all hover:-translate-y-1"
                    data-product-shop="true"
                  >
                    <div className="relative w-full aspect-video overflow-hidden">
                      <img
                        src={video.image}
                        alt={video.title}
                        title={`${video.title} - 极速影视${category.name}`}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-black/60 text-background-50 text-xs font-medium backdrop-blur-sm">
                        {video.status}
                      </div>
                      <div className="absolute top-3 right-3 px-2 py-1 rounded-full bg-primary-500/90 text-background-50 text-xs font-medium flex items-center gap-1">
                        <i className="ri-star-fill text-[10px]"></i>
                        {video.rating}
                      </div>
                      {video.hot && (
                        <div className="absolute bottom-3 left-3 px-2 py-0.5 rounded bg-accent-500 text-background-50 text-[10px] font-bold">
                          HOT
                        </div>
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-500 text-background-50">
                          <i className="ri-play-fill text-2xl"></i>
                        </div>
                      </div>
                    </div>
                    <div className="p-4">
                      <h3 className="font-heading text-base font-bold text-foreground-900 mb-1 line-clamp-1">
                        {video.title}
                      </h3>
                      <p className="text-xs text-foreground-500 mb-2 line-clamp-2">
                        {video.description}
                      </p>
                      <div className="flex items-center gap-3 text-xs text-foreground-400">
                        <span>{video.year}</span>
                        {video.episodes > 1 && (
                          <>
                            <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
                            <span>{video.episodes}集</span>
                          </>
                        )}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-200">
                  <i className="ri-film-line text-3xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500">该分类暂无内容，敬请期待</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}